// client/src/components/admin/analytics/EventDetailsModal.js
import React from 'react'
import PropTypes from 'prop-types'
import {
  formatDateTime,
  getEventTypeIcon,
  getTagCategory,
  getTagCategoryColor,
} from '../../../utils/analyticsUtils'
import { copyToClipboard } from '../../../utils/analyticsHelpers'

/**
 * Modal component for displaying detailed telemetry event information.
 * Shows event metadata, user info, timestamp, and provides copy functionality.
 *
 * @param {Boolean} isOpen - Whether the modal is visible
 * @param {Function} onClose - Handler for closing the modal
 * @param {Object} event - Event data to display (optional)
 * @param {Function} onJumpToUser - Handler for navigating to user profile
 */
function EventDetailsModal({ isOpen, onClose, event = null, onJumpToUser }) {
  if (!isOpen || !event) {
    return null
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="eventDetailsModalTitle"
        aria-modal="true"
        data-testid="event-details-modal"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="eventDetailsModalTitle">
                <span className="me-2">
                  {getEventTypeIcon(event.event_type)}
                </span>
                Event Details
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                data-testid="modal-close-button"
              ></button>
            </div>
            <div className="modal-body">
              <div className="row mb-3">
                <div className="col-12 col-md-6">
                  <h6 className="fw-bold">Event Type</h6>
                  <div className="d-flex align-items-center">
                    <span
                      className="badge bg-primary me-2"
                      data-testid="event-type-badge"
                    >
                      {event.event_type}
                    </span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => copyToClipboard(event.event_type)}
                      title="Copy event type"
                      data-testid="copy-event-type-button"
                    >
                      📋
                    </button>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <h6 className="fw-bold">Category</h6>
                  <div className="d-flex align-items-center">
                    <span
                      className={`badge bg-${getTagCategoryColor(
                        getTagCategory(event.tag),
                      )} me-2`}
                      data-testid="event-category-badge"
                    >
                      {getTagCategory(event.tag)}
                    </span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => copyToClipboard(getTagCategory(event.tag))}
                      title="Copy category"
                      data-testid="copy-category-button"
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-12 col-md-6">
                  <h6 className="fw-bold">Event Tag</h6>
                  <div className="d-flex align-items-center">
                    <code className="me-2" data-testid="event-tag">
                      {event.tag}
                    </code>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => copyToClipboard(event.tag)}
                      title="Copy tag"
                      data-testid="copy-tag-button"
                    >
                      📋
                    </button>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <h6 className="fw-bold">Page</h6>
                  <div className="d-flex align-items-center">
                    <code className="me-2" data-testid="event-page">
                      {event.page || 'Not specified'}
                    </code>
                    {event.page && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => copyToClipboard(event.page)}
                        title="Copy page"
                        data-testid="copy-page-button"
                      >
                        📋
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-12 col-md-6">
                  <h6 className="fw-bold">User</h6>
                  {event.user_id ? (
                    <div className="d-flex align-items-center">
                      <span
                        className="badge bg-success me-2"
                        data-testid="user-id-badge"
                      >
                        {event.user_id}
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={() => copyToClipboard(String(event.user_id))}
                        title="Copy user ID"
                        data-testid="copy-user-id-button"
                      >
                        📋
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onJumpToUser(event.user_id)}
                        title="Jump to user management"
                        data-testid="jump-to-user-button"
                      >
                        👤 View User
                      </button>
                    </div>
                  ) : (
                    <span
                      className="badge bg-secondary"
                      data-testid="anonymous-badge"
                    >
                      Anonymous
                    </span>
                  )}
                </div>
                <div className="col-12 col-md-6">
                  <h6 className="fw-bold">Timestamp</h6>
                  <div className="d-flex align-items-center">
                    <small className="me-2" data-testid="event-timestamp">
                      {formatDateTime(event.timestamp)}
                    </small>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => copyToClipboard(event.timestamp)}
                      title="Copy timestamp"
                      data-testid="copy-timestamp-button"
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold mb-0">Full Event Data</h6>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() =>
                      copyToClipboard(JSON.stringify(event, null, 2))
                    }
                    title="Copy full event data"
                    data-testid="copy-full-json-button"
                  >
                    📋 Copy All JSON
                  </button>
                </div>
                <pre
                  className="event-json-display"
                  data-testid="full-event-json"
                >
                  {JSON.stringify(event, null, 2)}
                </pre>
              </div>

              {event.metadata && Object.keys(event.metadata).length > 0 && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-bold mb-0">Metadata</h6>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() =>
                        copyToClipboard(JSON.stringify(event.metadata, null, 2))
                      }
                      title="Copy metadata JSON"
                      data-testid="copy-metadata-button"
                    >
                      📋 Copy Metadata
                    </button>
                  </div>
                  <pre
                    className="event-json-display"
                    data-testid="metadata-json"
                  >
                    {JSON.stringify(event.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                data-testid="modal-close-footer-button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop show"
        onClick={handleBackdropClick}
        data-testid="modal-backdrop"
      ></div>
    </>
  )
}

EventDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.shape({
    id: PropTypes.number,
    event_type: PropTypes.string.isRequired,
    tag: PropTypes.string.isRequired,
    page: PropTypes.string,
    user_id: PropTypes.number,
    timestamp: PropTypes.string.isRequired,
    metadata: PropTypes.object,
  }),
  onJumpToUser: PropTypes.func.isRequired,
}

export default EventDetailsModal
